/**
 * @license Copyright (c) 2023-2025, CKSource Holding sp. z o.o. All rights reserved.
 * For licensing, see LICENSE.md.
 */

import { type ReactNode, useContext } from 'react';
import { SnippetsSection } from '../Snippets/SnippetsSection';
import type { SnippetsSectionProps } from '../Snippets/types';
import { SnippetsToggle } from '../Snippets/SnippetsToggle';

import {
	TECHNOLOGIES,
	INSTALL_METHODS,
	CODE_OUTPUT_OPTIONS
} from '../../../constants/installation';
import { SECTION_NAME_SETUP } from '../../../constants/snippetsSectionNames';
import { InstallationSettingsContext } from '../../../contexts/InstallationSettingsContext';
import { CustomerPortalContext } from '../../../contexts/CustomerPortalContext';
import { Typography } from '@mui/material';
import { getInstallationMethodToggleStatus } from '../../../utils/getInstallationMethodToggleStatus/getInstallationMethodToggleStatus';
import { getCodeOutputToggleStatus } from '../../../utils/getCodeOutputToggleStatus/getCodeOutputToggleStatus';
import { isCloudMethod } from '../../../utils/isCloudMethod/isCloudMethod';
import { isSelfHostedMethod } from '../../../utils/isSelfHostedMethod/isSelfHostedMethod';

const docsUrl = 'https://ckeditor.com/docs/ckeditor5/latest/getting-started/licensing/license-key-and-activation.html';

export function SnippetsTechnologySection( {
	number,
	onTechnologyChange,
	onInstallMethodChange,
	onCodeOutputChange,
	hasPremiumPlugins
}: SnippetsSectionProps ): ReactNode {
	const { selectedTechnology, selectedInstallMethod, selectedCodeOutput } = useContext( InstallationSettingsContext );
	const { hasSelfHostedAccess, hasCloudAccess, hasOldGPLAccess, isLoggedIn } = useContext( CustomerPortalContext );
	const selectedInstallMethodName = INSTALL_METHODS.find( installMethod => installMethod.key === selectedInstallMethod )?.name;
	const showTrialNote = isLoggedIn && (
		( !hasCloudAccess && isCloudMethod( selectedInstallMethod ) ) ||
		( !hasSelfHostedAccess && !hasOldGPLAccess && isSelfHostedMethod( selectedInstallMethod ) )
	);

	return (
		<SnippetsSection
			headerCharacter={ number }
			title={ SECTION_NAME_SETUP }
			hideHorizontalLine={ true }
		>
			<SnippetsSection
				headerCharacter={ '' }
				title={ 'Pick technology:' }
				variant={ 'small' }
			>
				<SnippetsToggle
					items={ TECHNOLOGIES }
					selectedItem={ selectedTechnology }
					hasPremiumPlugins={ hasPremiumPlugins }
					onSelectionChange={ onTechnologyChange }
					toggleDisabledStatusCallback={ () => ( { disabled: false, tooltip: null } ) }
				/>
			</SnippetsSection>
			<SnippetsSection
				headerCharacter={ '' }
				title={ 'Select integration method:' }
				variant={ 'small' }
			>
				<SnippetsToggle
					items={ INSTALL_METHODS }
					selectedItem={ selectedInstallMethod }
					hasPremiumPlugins={ hasPremiumPlugins }
					onSelectionChange={ onInstallMethodChange }
					toggleDisabledStatusCallback={ getInstallationMethodToggleStatus }
				/>
				{ showTrialNote && (
					<Typography color={ 'text.secondary' } fontSize={ '14px' } fontWeight={ 400 } lineHeight={ '22px' } marginTop={ '2px' }>
						<b>Note:</b> The { selectedInstallMethodName } method will only be available during
						the trial period.<br/>Once the trial ends, your editor will no longer function with this
						setup. <a href={ docsUrl } target='_blank'>Read more in the docs</a>.
					</Typography>
				) }
			</SnippetsSection>
			<SnippetsSection
				headerCharacter={ '' }
				title={ 'Choose output:' }
				variant={ 'small' }
				hideHorizontalLine={ true }
			>
				<SnippetsToggle
					items={ CODE_OUTPUT_OPTIONS }
					selectedItem={ selectedCodeOutput }
					hasPremiumPlugins={ hasPremiumPlugins }
					onSelectionChange={ onCodeOutputChange }
					toggleDisabledStatusCallback={ getCodeOutputToggleStatus }
				/>
			</SnippetsSection>
		</SnippetsSection>
	);
}
